import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton } from "../components/neu/Neu";
import { useAuth } from "../context/AuthContext";
import { useCan } from "../hooks/useCan";
import { approvePenalty, cancelPenalty, deletePenalty, rejectPenalty } from "../api/penalties";
import { usePenalties } from "../features/penalties/usePenalties";
import { PenaltySummary } from "../features/penalties/PenaltySummary";
import { PenaltyFilters } from "../features/penalties/PenaltyFilters";
import { PenaltyForm } from "../features/penalties/PenaltyForm";
import { PenaltyTable } from "../features/penalties/PenaltyTable";
import { PenaltyDetail } from "../features/penalties/PenaltyDetail";

import { ADMIN_ROLES, MANAGER_ROLES } from "../utils/role";

const CREATORS = MANAGER_ROLES;

export default function PenaltiesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const state = usePenalties();
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const { can: canOverride } = useCan("hr.penalties.override");

  const run = async (fn, key) => {
    try {
      await fn();
      toast.success(t(`penalty.done.${key}`));
      state.reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("penalty.actionFailed"));
    }
  };

  const reject = (row) => {
    const reason = window.prompt(t("penalty.actions.rejectReason"));
    if (reason && reason.trim()) run(() => rejectPenalty(row.penalty_id, reason.trim()), "rejected");
  };

  return (
    <AppShell
      title={t("penalty.title")}
      subtitle={t("penalty.subtitle")}
      actions={
        CREATORS.includes(user?.role) ? (
          <NeuButton variant="primary" data-testid="penalty-issue-btn" className="flex items-center gap-2"
                     onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> {t("penalty.issue")}
          </NeuButton>
        ) : null
      }
    >
      <div className="settings-zoom grid gap-5" data-testid="penalties-page">
        <PenaltySummary rows={state.rows} branches={state.branches} ready={state.refsLoaded} />
        <PenaltyFilters filters={state.filters} setFilters={state.setFilters} types={state.types}
                        employees={state.employees} branches={state.branches} />

        {creating ? (
          <PenaltyForm employees={state.employees} branches={state.branches} departments={state.departments}
                       reasons={state.reasons} canOverride={canOverride}
                       onSaved={state.reload} onClose={() => setCreating(false)} />
        ) : null}

        <PenaltyTable
          rows={state.rows}
          isAdmin={isAdmin}
          onApprove={(row) => run(() => approvePenalty(row.penalty_id), "approved")}
          onReject={reject}
          onCancel={(row) => run(() => cancelPenalty(row.penalty_id), "cancelled")}
          onView={(row) => setViewing(row.penalty_id)}
        />
        <PenaltyDetail
          penaltyId={viewing}
          isAdmin={isAdmin}
          onClose={() => setViewing(null)}
          onCancel={() => { run(() => cancelPenalty(viewing), "cancelled"); setViewing(null); }}
          onDelete={() => { run(() => deletePenalty(viewing), "deleted"); setViewing(null); }}
        />
      </div>
    </AppShell>
  );
}
